import type { ApiErrorResponse, ApiErrorCode } from "@/types/api";

export class ApiClientError extends Error {
  readonly code: ApiErrorCode;
  readonly fieldErrors: Record<string, string[]> | undefined;
  readonly status: number;

  constructor(status: number, code: ApiErrorCode, message: string, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

export type QueryParamValue = string | number | boolean | string[] | number[] | undefined;

export function buildQueryString(params: Record<string, QueryParamValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(","));
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

async function parseErrorResponse(response: Response): Promise<ApiClientError> {
  try {
    const body = (await response.json()) as ApiErrorResponse;
    return new ApiClientError(
      response.status,
      body.error?.code ?? "INTERNAL_ERROR",
      body.error?.message ?? "Request failed",
      body.error?.fieldErrors,
    );
  } catch {
    return new ApiClientError(response.status, "INTERNAL_ERROR", "Request failed");
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();

  // In React 18 development, Strict Mode intentionally double-invokes effects.
  // To avoid hammering the backend with identical in-flight requests, we
  // dedupe concurrent GETs that have the same URL.
  //
  // We explicitly opt-out when the caller provides a `signal`, because
  // aborting one consumer would abort the shared request for all others.
  const canDedupeGet =
    method === "GET" &&
    init?.signal === undefined &&
    // Body-bearing GETs are atypical; don't try to guess correctness.
    init?.body === undefined;

  const makeRequestKey = (): string => `GET:${path}`;

  if (canDedupeGet) {
    const key = makeRequestKey();

    const cached = completedGetCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data as T;
    }

    const existing = inFlightGetRequests.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    const promise = (async () => {
      const response = await fetch(path, {
        ...init,
        headers: {
          Accept: "application/json",
          ...init?.headers,
        },
      });

      if (!response.ok) {
        throw await parseErrorResponse(response);
      }

      const data = (await response.json()) as T;
      completedGetCache.set(key, { expiresAt: Date.now() + GET_CACHE_TTL_MS, data });
      return data;
    })();

    inFlightGetRequests.set(key, promise as Promise<unknown>);
    promise.finally(() => {
      inFlightGetRequests.delete(key);
    });

    return promise;
  }

  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  return (await response.json()) as T;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

const inFlightGetRequests: Map<string, Promise<unknown>> = new Map();
const completedGetCache: Map<string, { expiresAt: number; data: unknown }> = new Map();
const GET_CACHE_TTL_MS = 30_000;
